import { Alert, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type { BillingDemoDetails, MeteredUsage } from 'api-client';
export default function MeteredUsagePanel({
  data,
  itemId,
  quantity,
  pending,
  error,
  receipt,
  timeZone,
  onItem,
  onQuantity,
  onSubmit,
}: {
  data: BillingDemoDetails;
  itemId: string;
  quantity: string;
  pending: boolean;
  error: string | null;
  receipt: MeteredUsage | null;
  timeZone?: string;
  onItem: (value: string) => void;
  onQuantity: (value: string) => void;
  onSubmit: () => void;
}) {
  const allowed = data.billingEnabled && data.hasAccess && data.meteredItems.length > 0;
  const valid =
    Number.isSafeInteger(Number(quantity)) &&
    Number(quantity) > 0 &&
    data.meteredItems.some((item) => item.value === itemId);
  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}
      {receipt && (
        <Alert severity="success">
          Recorded {receipt.quantity} units. Confirmation: {receipt.id}
        </Alert>
      )}
      {!allowed ? (
        <Alert severity="info">
          {!data.billingEnabled
            ? 'Metered usage reporting is unavailable during beta.'
            : 'No active metered subscription is available.'}
        </Alert>
      ) : (
        <Stack
          component="form"
          spacing={2}
          onSubmit={(event) => {
            event.preventDefault();
            if (valid && !pending) onSubmit();
          }}
        >
          <TextField
            select
            label="Metered product"
            value={itemId}
            onChange={(event) => onItem(event.target.value)}
            disabled={pending}
          >
            {data.meteredItems.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Usage quantity"
            type="number"
            value={quantity}
            onChange={(event) => onQuantity(event.target.value)}
            disabled={pending}
            helperText="Enter a positive whole number of units."
            slotProps={{ htmlInput: { min: 1, step: 1 } }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={pending || !valid}
            loading={pending}
            loadingPosition="start"
          >
            {pending ? 'Recording usage…' : 'Record usage'}
          </Button>
        </Stack>
      )}
      <Typography component="h2" variant="h6">
        Recent usage records
      </Typography>
      {data.usageRecords.length === 0 ? (
        <Typography>No recorded usage available.</Typography>
      ) : (
        data.usageRecords.map((record) => (
          <Stack key={record.id} spacing={1} sx={{ p: 2, bgcolor: 'surfaces.surfaceRaised', overflowWrap: 'anywhere' }}>
            <Typography>
              {record.quantity} units · {record.id}
            </Typography>
            {record.timestamp && (
              <Typography
                component="time"
                dateTime={record.timestamp.toISOString()}
                variant="body2"
                color="text.secondary"
              >
                {record.timestamp.toLocaleString(undefined, { timeZone })}
              </Typography>
            )}
          </Stack>
        ))
      )}
    </Stack>
  );
}
